import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { CrewMember, ShiftEntry } from "@/mocks/admin/crew";

type Project = {
  id: string;
  customer_name: string;
  move_type: string;
  move_date: string;
  move_time: string;
  from_city: string;
  to_city: string;
  truck: string;
  status: string;
};

type Props = {
  member: CrewMember;
  shifts: ShiftEntry[];
  refetchShifts: () => Promise<void> | void;
  onToast: (msg: string) => void;
};

function nowHHmm() {
  return new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function todayLabel() {
  return new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

function diffHoursFromClockIn(clockIn: string): { h: number; m: number; s: number; totalHours: number } {
  const [hh, mm] = clockIn.split(":").map(Number);
  const start = new Date();
  start.setHours(hh, mm, 0, 0);
  const ms = Math.max(0, new Date().getTime() - start.getTime());
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return { h, m, s, totalHours: ms / 3600000 };
}

export default function TodayTab({ member, shifts, refetchShifts, onToast }: Props) {
  const [project, setProject] = useState<Project | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  // pull the active shift for this mover (active OR on-break)
  const activeShift = useMemo(
    () =>
      shifts.find(
        (s) => s.crewId === member.id && (s.status === "active" || s.status === "on-break"),
      ) || null,
    [shifts, member.id],
  );

  // current project id: prefer the one on the shift, fallback to member.currentProject
  const projectId = activeShift?.projectId || member.currentProject || null;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!projectId) {
        setProject(null);
        return;
      }
      const { data } = await supabase
        .from("moving_projects")
        .select("id, customer_name, move_type, move_date, move_time, from_city, to_city, truck, status")
        .eq("id", projectId)
        .maybeSingle();
      if (!cancelled) setProject((data as Project) || null);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // live clock when on shift
  useEffect(() => {
    if (!activeShift) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [activeShift]);

  const elapsed = activeShift
    ? diffHoursFromClockIn(activeShift.clockIn)
    : { h: 0, m: 0, s: 0, totalHours: 0 };

  const earningsSoFar = activeShift ? elapsed.totalHours * member.hourlyRate : 0;

  const clockIn = async () => {
    if (busy) return;
    setBusy(true);
    const id = `S-${Date.now().toString().slice(-6)}`;
    const payload = {
      id,
      crew_id: member.id,
      crew_name: member.name,
      initials: member.initials,
      color: member.color,
      role: member.role,
      project_id: projectId || "—",
      clock_in: nowHHmm(),
      clock_out: null,
      break_minutes: 0,
      total_hours: null,
      status: "active",
      hourly_rate: member.hourlyRate,
      shift_date: todayLabel(),
      flag: null,
    };
    const { error } = await supabase.from("shifts").insert(payload);
    if (error) {
      onToast(error.message);
    } else {
      onToast("Clocked in. Have a great shift!");
      await refetchShifts();
    }
    setBusy(false);
  };

  const toggleBreak = async () => {
    if (!activeShift || busy) return;
    setBusy(true);
    const newStatus = activeShift.status === "on-break" ? "active" : "on-break";
    const newBreak =
      activeShift.status === "on-break" ? activeShift.break : (activeShift.break || 0) + 15;
    const { error } = await supabase
      .from("shifts")
      .update({ status: newStatus, break_minutes: newBreak })
      .eq("id", activeShift.id);
    if (error) onToast(error.message);
    else {
      onToast(newStatus === "on-break" ? "Break started" : "Welcome back!");
      await refetchShifts();
    }
    setBusy(false);
  };

  const clockOut = async () => {
    if (!activeShift || busy) return;
    setBusy(true);
    const totalHours = Math.round(elapsed.totalHours * 100) / 100;
    const { error } = await supabase
      .from("shifts")
      .update({
        status: "pending-approval",
        clock_out: nowHHmm(),
        total_hours: totalHours,
      })
      .eq("id", activeShift.id);
    if (error) onToast(error.message);
    else {
      onToast(`Shift submitted · ${totalHours}h logged`);
      await refetchShifts();
    }
    setBusy(false);
  };

  const onBreak = activeShift?.status === "on-break";

  return (
    <div className="px-5 py-5 space-y-5">
      {/* Big clock-in / out card */}
      <section
        className={`rounded-2xl p-5 text-white relative overflow-hidden ${
          activeShift
            ? onBreak
              ? "bg-gradient-to-br from-amber-600 via-amber-700 to-orange-700"
              : "bg-gradient-to-br from-brand-600 via-brand-700 to-orange-800"
            : "bg-gradient-to-br from-ink-800 via-ink-900 to-ink-900"
        }`}
      >
        <div className="absolute -right-10 -top-12 w-44 h-44 rounded-full bg-white/10 blur-3xl"></div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
              {activeShift ? (onBreak ? "On Break" : "On The Clock") : "Off Clock"}
            </span>
            {activeShift && (
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            )}
          </div>

          {activeShift ? (
            <>
              <div
                key={tick}
                className="font-display text-5xl font-bold tabular-nums tracking-tight leading-none mt-1"
              >
                {String(elapsed.h).padStart(2, "0")}:{String(elapsed.m).padStart(2, "0")}
                <span className="text-2xl opacity-70">:{String(elapsed.s).padStart(2, "0")}</span>
              </div>
              <div className="text-[11px] text-white/80 mt-1.5">
                Started at {activeShift.clockIn}
                {activeShift.break > 0 && ` · ${activeShift.break}m break taken`}
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/15">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">
                    Earnings so far
                  </div>
                  <div className="font-display text-xl font-bold tabular-nums mt-0.5">
                    ${earningsSoFar.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">
                    Rate
                  </div>
                  <div className="font-display text-xl font-bold tabular-nums mt-0.5">
                    ${member.hourlyRate}/h
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-5">
                <button
                  onClick={toggleBreak}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-white/15 hover:bg-white/25 text-sm font-bold cursor-pointer whitespace-nowrap backdrop-blur-sm disabled:opacity-60"
                >
                  <i className={onBreak ? "ri-play-fill" : "ri-pause-fill"}></i>
                  {onBreak ? "Resume" : "Break"}
                </button>
                <button
                  onClick={clockOut}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-white text-ink-900 hover:bg-cream-100 text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  <i className="ri-logout-box-r-line"></i>
                  Clock Out
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="font-display text-3xl font-bold leading-tight mt-1">
                {projectId ? "Ready to start?" : "No job today"}
              </div>
              <p className="text-[12px] text-white/70 mt-2 leading-relaxed">
                {projectId
                  ? "Tap below when you arrive on site. We'll start the timer and track your earnings."
                  : "You don't have an assigned job right now. Check the Jobs tab for upcoming work."}
              </p>
              <button
                onClick={clockIn}
                disabled={busy || !projectId}
                className="w-full mt-5 inline-flex items-center justify-center gap-2 py-4 rounded-xl bg-white text-ink-900 hover:bg-cream-100 text-base font-bold cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-login-box-line text-lg"></i>
                Clock In
              </button>
            </>
          )}
        </div>
      </section>

      {/* Current / next assignment */}
      <section>
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="font-display text-base font-bold text-ink-900">
            {activeShift ? "Current Job" : "Today's Assignment"}
          </h3>
          {project && (
            <span className="font-mono text-[10px] font-bold text-brand-700 bg-brand-100 px-2 py-1 rounded-full">
              {project.id}
            </span>
          )}
        </div>

        {project ? (
          <div className="bg-white border border-ink-100 rounded-2xl p-4 space-y-4">
            <div>
              <div className="font-display text-base font-bold text-ink-900">
                {project.customer_name}
              </div>
              <div className="text-[12px] text-ink-500">{project.move_type}</div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 shrink-0 mt-0.5">
                  <i className="ri-arrow-up-circle-line text-base"></i>
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                    Pick up
                  </div>
                  <div className="text-sm font-semibold text-ink-900 leading-tight mt-0.5">
                    {project.from_city}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-50 text-brand-600 shrink-0 mt-0.5">
                  <i className="ri-arrow-down-circle-line text-base"></i>
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                    Drop off
                  </div>
                  <div className="text-sm font-semibold text-ink-900 leading-tight mt-0.5">
                    {project.to_city}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-ink-100">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                  Date
                </div>
                <div className="text-xs font-bold text-ink-900 mt-0.5">{project.move_date}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                  Time
                </div>
                <div className="text-xs font-bold text-ink-900 mt-0.5">{project.move_time}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                  Truck
                </div>
                <div className="text-xs font-bold text-ink-900 mt-0.5 truncate">{project.truck}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <a
                href={`tel:5125550100`}
                className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-cream-100 text-ink-700 hover:bg-cream-200 text-xs font-bold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                Call Lead
              </a>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(project.from_city)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-ink-900 hover:bg-ink-800 text-white text-xs font-bold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-navigation-line"></i>
                Navigate
              </a>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-dashed border-ink-200 rounded-2xl p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-cream-100 text-ink-400 flex items-center justify-center">
              <i className="ri-calendar-todo-line text-xl"></i>
            </div>
            <div className="text-sm font-bold text-ink-900 mt-2">Nothing scheduled</div>
            <p className="text-[11px] text-ink-500 mt-1">Enjoy the day off!</p>
          </div>
        )}
      </section>

      {/* This week mini stats */}
      <section className="grid grid-cols-3 gap-2.5">
        <div className="bg-white border border-ink-100 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">Week</div>
          <div className="font-display text-lg font-bold text-ink-900 mt-0.5 tabular-nums">
            {member.weeklyHours}h
          </div>
          <div className="text-[10px] text-ink-500">of {member.weeklyTarget}h</div>
        </div>
        <div className="bg-white border border-ink-100 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">Rate</div>
          <div className="font-display text-lg font-bold text-ink-900 mt-0.5 tabular-nums">
            ${member.hourlyRate}
          </div>
          <div className="text-[10px] text-ink-500">per hour</div>
        </div>
        <div className="bg-white border border-ink-100 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">Rating</div>
          <div className="font-display text-lg font-bold text-ink-900 mt-0.5 tabular-nums">
            {member.rating.toFixed(1)}
          </div>
          <div className="text-[10px] text-amber-600 font-semibold flex items-center gap-0.5">
            <i className="ri-star-fill text-[10px]"></i>
            <i className="ri-star-fill text-[10px]"></i>
            <i className="ri-star-fill text-[10px]"></i>
            <i className="ri-star-fill text-[10px]"></i>
            <i className="ri-star-half-fill text-[10px]"></i>
          </div>
        </div>
      </section>
    </div>
  );
}